/**
 * Componente Input
 * Wrapper para o Input do Ant Design
 * Atualizado para remover props deprecated (addonAfter, addonBefore)
 */

import React from "react";
import { Input as AntInput, Form, Space } from "antd";

const Input = ({
  label,
  error,
  helperText,
  icon: Icon,
  iconPosition = "left",
  fullWidth = false,
  className,
  id,
  required,
  value,
  onChange,
  addonAfter,
  addonBefore,
  useFormItem = true, // Nova prop para controlar se usa Form.Item
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  // Filtrar props deprecated (addonAfter, addonBefore) do spread props
  const {
    addonAfter: _addonAfterFromProps,
    addonBefore: _addonBeforeFromProps,
    ...cleanProps
  } = props;

  // Garantir que addonAfter e addonBefore não sejam passados para o AntInput
  const {
    addonAfter: _,
    addonBefore: __,
    ...finalCleanProps
  } = cleanProps;

  // Mescla estilos: fullWidth e style customizado (se fornecido)
  const { style: customStyle, ...restProps } = finalCleanProps;
  const mergedStyle = fullWidth 
    ? { width: "100%", ...(customStyle || {}) }
    : (customStyle || undefined);

  const inputProps = {
    id: inputId,
    status: error ? "error" : undefined,
    prefix: Icon && iconPosition === "left" ? <Icon /> : undefined,
    suffix: Icon && iconPosition === "right" ? <Icon /> : undefined,
    className,
    style: mergedStyle,
    value,
    onChange,
    ...restProps,
  };

  // Se tiver addonAfter ou addonBefore, usar Space.Compact (recomendação do Ant Design)
  // Priorizar props diretas sobre props do spread
  const finalAddonAfter = addonAfter || _addonAfterFromProps;
  const finalAddonBefore = addonBefore || _addonBeforeFromProps;
  const hasAddon = finalAddonAfter || finalAddonBefore;
  
  const inputElement = hasAddon ? (
    <Space.Compact style={{ width: "100%" }}>
      {finalAddonBefore && (
        <span style={{ 
          padding: "4px 11px", 
          border: "1px solid #d9d9d9", 
          borderRight: "none", 
          borderRadius: "6px 0 0 6px", 
          background: "#fafafa",
          display: "flex",
          alignItems: "center"
        }}>
          {finalAddonBefore}
        </span>
      )}
      <AntInput 
        {...inputProps} 
        style={{ 
          ...inputProps.style, 
          borderRadius: finalAddonBefore && finalAddonAfter ? 0 : finalAddonBefore ? "0 6px 6px 0" : finalAddonAfter ? "6px 0 0 6px" : "6px" 
        }} 
      />
      {finalAddonAfter && (
        <span style={{ 
          padding: "4px 11px", 
          border: "1px solid #d9d9d9", 
          borderLeft: "none", 
          borderRadius: "0 6px 6px 0", 
          background: "#fafafa",
          display: "flex",
          alignItems: "center"
        }}>
          {finalAddonAfter}
        </span>
      )}
    </Space.Compact>
  ) : (
    <AntInput {...inputProps} />
  );

  // Se tiver label e useFormItem for true, usa Form.Item para manter compatibilidade
  if (label && useFormItem) {
    return (
      <Form.Item
        label={label}
        required={required}
        validateStatus={error ? "error" : undefined}
        help={error || helperText}
        style={{ marginBottom: 20, width: fullWidth ? "100%" : undefined }}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {inputElement}
      </Form.Item>
    );
  }

  // Se tiver label mas useFormItem for false, renderiza label manualmente
  if (label && !useFormItem) {
    return (
      <div style={{ marginBottom: 20, width: fullWidth ? "100%" : undefined }}>
        <div style={{ marginBottom: 8 }}>
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            style={{
              marginBottom: 0
            }}
          >
            {label}
            {required && <span className="text-red-500 dark:text-red-400" style={{ marginLeft: 4 }}>*</span>}
          </label>
        </div>
        {inputElement}
        {(error || helperText) && (
          <div className={`mt-1 text-xs ${error ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  }

  // Sem label, retorna apenas o Input
  return inputElement;
};

export default Input;
